import { useAgentTarget } from "@/OneLiners";
import {
    ActionResponse,
    ActionStatusHelper,
    DOMAIN_CONFIGS,
    DomainFormHook,
    DomainHookProps,
    useFormLifeCycle,
} from "@/Shared";

import { AgentTargetFormViewModel } from "../ViewModels";
import { useAgentTargetEnricher } from "./useAgentTrgetEnricher";
import { AgentTargetValidationSchema } from "../AgentTargetFormValidation";
import { AgentTargetModel } from "../AgentTargetModel";


/**
 * useAgentTargetForm hook – orchestrates agentTarget form initialization and submission.
 * 
 * Author: Samano CASTRE  
 * Date: July 17, 2025
 * 
 * Responsibilities:
 * - Coordinates permissions, domain state and enrichment layers
 * - Builds and exposes the AgentTargetFormViewModel to the UI
 * - Delegates submission logic and feedback handling to dedicated hooks
 * 
 * Design notes:
 * - This hook does not perform CRUD or permission resolution directly
 * - It acts as an orchestration layer between UI and domain-specific hooks
 */
export const useAgentTargetForm = (props: DomainHookProps): DomainFormHook => {
    const { extraArgs, permissionHook } = props;
    const { user } = extraArgs ?? {};
    const { domainType } = DOMAIN_CONFIGS.AgentTarget;
    /**
     * AgentTarget domain operations and related backend state.
     * Exposes CRUD actions, current entity fetching and associated responses.
     */
    const agentTargetHook = useAgentTarget();

    /**
        * Enrichment layer
        *
        * Responsible for resolving user-related data and
        * computing derived BirthWish fields.
        */
    const {
        userMap,
        getEnrichedAgentTarget,
        enricherResponse,
    } = useAgentTargetEnricher();


    /**
     * Aggregated response representing the global initialization state.
     * Combines:
     * - agentTarget domain state
     * - permission loading state
     * - enrichment layer state
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            agentTargetHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: agentTargetHook.stateResponse.error ??
            enricherResponse.error
    };

    const lifeCycle = useFormLifeCycle<AgentTargetFormViewModel, AgentTargetModel>({
        response,
        domainType,
        props,
        entityHook: agentTargetHook,
        buildVM: (currentEntity: AgentTargetModel) => {
            return new AgentTargetFormViewModel(
                userMap,
                getEnrichedAgentTarget(currentEntity),
                user
            )
        }
    });

    /**
     * Public API exposed to the component layer.
     * Only data and actions strictly required by the UI are returned.
     */
    return {
        ...lifeCycle,
        ValidationSchema: AgentTargetValidationSchema(),
    }
}